import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { airportTrip, formatUsd, type Estimate, type RateCardInput } from '@rydres/shared';
import { AddressInput, Button, Card, Field, InfoTip, Input, Notice, driveText, useRoute, type PickedAddress } from '@rydres/ui';
import { geoApi, remindersApi } from '../lib/api.js';

const NOWHERE: PickedAddress = { address: '', point: null };

const toDollars = (c: number) => (c / 100).toString();
const toCents = (s: string) => Math.round(Number(s || 0) * 100);

/** Private: only the driver sees this. The result is a suggestion to type into a quote. */
export function Calculator() {
  const navigate = useNavigate();
  const [card, setCard] = useState<(RateCardInput & { saved: boolean }) | null>(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [q, setQ] = useState({ mode: 'distance' as 'distance' | 'hourly', miles: '', minutes: '', hours: '', airport: false, waitMinutes: '', roundToDollars: '5' });
  const [result, setResult] = useState<Estimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Two places fill in the miles and minutes from the real drive.
  const [trip, setTrip] = useState({ from: NOWHERE, to: NOWHERE });
  const drive = useRoute(geoApi, trip.from.point, trip.to.point);
  useEffect(() => {
    if (drive)
      setQ((x) => ({ ...x, mode: 'distance', miles: String(Math.round(drive.miles)), minutes: String(drive.minutes), airport: x.airport || airportTrip([trip.from, trip.to]) }));
  }, [drive]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    remindersApi.rateCard().then((c) => {
      setCard(c);
      setForm({ hourly: toDollars(c.hourlyCents), minHours: String(c.minHours), perMile: toDollars(c.perMileCents), perMinute: toDollars(c.perMinuteCents), minFare: toDollars(c.minFareCents), airport: toDollars(c.airportFeeCents), wait: toDollars(c.waitPerMinCents), gratuity: String(c.gratuityPct), notes: c.notes ?? '' });
      if (!c.saved) setEdit(true);
    }, () => undefined);
  }, []);

  const saveCard = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const c = await remindersApi.saveRateCard({
        hourlyCents: toCents(form.hourly!), minHours: Number(form.minHours), perMileCents: toCents(form.perMile!), perMinuteCents: toCents(form.perMinute!),
        minFareCents: toCents(form.minFare!), airportFeeCents: toCents(form.airport!), waitPerMinCents: toCents(form.wait!), gratuityPct: Number(form.gratuity), notes: form.notes || null,
      });
      setCard(c);
      setEdit(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };
  const run = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      setResult(await remindersApi.estimate({
        mode: q.mode, miles: Number(q.miles || 0), minutes: Number(q.minutes || 0), hours: Number(q.hours || 0), airport: q.airport, waitMinutes: Number(q.waitMinutes || 0), roundToDollars: Number(q.roundToDollars || 0),
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not calculate.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
        <h1 className="text-xl font-black">Price calculator</h1>
        <InfoTip label="Price calculator">Your own rates, for your eyes only. Riders see the price you type into a quote, never this sheet.</InfoTip>
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      {card && (
        <Card title="My rates" right={<button className="text-sm font-bold text-accent-text" onClick={() => setEdit(!edit)}>{edit ? 'Close' : 'Edit'}</button>}>
          {edit ? (
            <form onSubmit={saveCard} className="grid grid-cols-2 gap-3">
              <Field label="Hourly $"><Input type="number" step="1" value={form.hourly} onChange={(e) => setForm({ ...form, hourly: e.target.value })} /></Field>
              <Field label="Minimum hours"><Input type="number" step="0.5" value={form.minHours} onChange={(e) => setForm({ ...form, minHours: e.target.value })} /></Field>
              <Field label="Per mile $"><Input type="number" step="0.05" value={form.perMile} onChange={(e) => setForm({ ...form, perMile: e.target.value })} /></Field>
              <Field label="Per minute $"><Input type="number" step="0.05" value={form.perMinute} onChange={(e) => setForm({ ...form, perMinute: e.target.value })} /></Field>
              <Field label="Minimum fare $"><Input type="number" step="1" value={form.minFare} onChange={(e) => setForm({ ...form, minFare: e.target.value })} /></Field>
              <Field label="Airport fee $"><Input type="number" step="1" value={form.airport} onChange={(e) => setForm({ ...form, airport: e.target.value })} /></Field>
              <Field label="Wait, per minute $"><Input type="number" step="0.05" value={form.wait} onChange={(e) => setForm({ ...form, wait: e.target.value })} /></Field>
              <Field label="Gratuity %"><Input type="number" step="1" value={form.gratuity} onChange={(e) => setForm({ ...form, gratuity: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Holiday surcharge, tolls, …" /></Field></div>
              <div className="col-span-2"><Button type="submit" className="w-full">Save rates</Button></div>
            </form>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted">Hourly</dt><dd className="font-bold">{formatUsd(card.hourlyCents)}/hr, {card.minHours} hr min</dd>
              <dt className="text-muted">Distance</dt><dd className="font-bold">{formatUsd(card.perMileCents)}/mi + {formatUsd(card.perMinuteCents)}/min</dd>
              <dt className="text-muted">Minimum</dt><dd className="font-bold">{formatUsd(card.minFareCents)}</dd>
              <dt className="text-muted">Airport / wait</dt><dd className="font-bold">{formatUsd(card.airportFeeCents)} / {formatUsd(card.waitPerMinCents)} per min</dd>
              {card.gratuityPct > 0 && <><dt className="text-muted">Gratuity</dt><dd className="font-bold">{card.gratuityPct}%</dd></>}
            </dl>
          )}
        </Card>
      )}
      <form onSubmit={run}>
        <Card title="Work out a price">
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <Field label="From"><AddressInput geo={geoApi} near={trip.to.point} value={trip.from.address} onChange={(from) => setTrip({ ...trip, from })} placeholder="Pickup, to fill in the miles" /></Field>
            <Field label="To"><AddressInput geo={geoApi} near={trip.from.point} value={trip.to.address} onChange={(to) => setTrip({ ...trip, to })} placeholder="Drop-off" /></Field>
            {drive && <p className="text-xs text-muted sm:col-span-2">{driveText(drive)}. Miles and minutes are filled in below.</p>}
          </div>
          <div className="mb-3 flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
            {(['distance', 'hourly'] as const).map((m) => <button type="button" key={m} onClick={() => setQ({ ...q, mode: m })} className={`flex-1 rounded-full px-3 py-1.5 capitalize ${q.mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>{m}</button>)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {q.mode === 'distance' ? (
              <>
                <Field label="Miles"><Input type="number" step="1" value={q.miles} onChange={(e) => setQ({ ...q, miles: e.target.value })} /></Field>
                <Field label="Minutes"><Input type="number" step="5" value={q.minutes} onChange={(e) => setQ({ ...q, minutes: e.target.value })} /></Field>
              </>
            ) : (
              <Field label="Hours"><Input type="number" step="0.5" value={q.hours} onChange={(e) => setQ({ ...q, hours: e.target.value })} /></Field>
            )}
            <Field label="Wait (minutes)"><Input type="number" step="5" value={q.waitMinutes} onChange={(e) => setQ({ ...q, waitMinutes: e.target.value })} /></Field>
            <Field label="Round up to $"><Input type="number" step="1" value={q.roundToDollars} onChange={(e) => setQ({ ...q, roundToDollars: e.target.value })} /></Field>
            <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={q.airport} onChange={(e) => setQ({ ...q, airport: e.target.checked })} /> Airport pickup or drop-off</label>
          </div>
          <Button type="submit" className="mt-3 w-full">Calculate</Button>
          {result && (
            <div className="mt-4 rounded-2xl bg-card2 p-4 text-sm">
              {result.lines.map((l, i) => <div key={i} className="flex justify-between py-0.5"><span className="text-ink2">{l.label}</span><span>{formatUsd(l.cents)}</span></div>)}
              {result.gratuityCents > 0 && <div className="flex justify-between py-0.5"><span className="text-ink2">Gratuity</span><span>{formatUsd(result.gratuityCents)}</span></div>}
              <div className="mt-2 flex items-center justify-between border-t border-line pt-2"><span className="flex items-center gap-1 font-bold">Suggested price<InfoTip label="Suggested price">Type this into the quote, or adjust it. The rider only ever sees the final number.</InfoTip></span><span className="text-2xl font-black">{formatUsd(result.totalCents)}</span></div>
            </div>
          )}
        </Card>
      </form>
    </div>
  );
}
